"use client"

import { FEATURE_CATALOG, FEATURE_KEYS, type FeatureKey, type SuperAdminClientDetail } from "@repo/types"
import { toast } from "sonner"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select"
import { useSuperAdminClientActions } from "../../hooks/api/superadmin/use-superadmin-client"
import { apiErrorMessage } from "./client-detail.helpers"

type Choice = "default" | "on" | "off"

const toChoice = (override: boolean | undefined): Choice => (override === undefined ? "default" : override ? "on" : "off")
const toOverride = (choice: Choice): boolean | null => (choice === "default" ? null : choice === "on")

/** Fiche client : exceptions aux fonctionnalités par défaut (réglées dans /superadmin/features). */
export function ClientFeaturesCard({ client }: { client: SuperAdminClientDetail }) {
  const { updateFeatures } = useSuperAdminClientActions(client.id)
  const { overrides, effective } = client.features

  const change = async (key: FeatureKey, choice: Choice) => {
    try {
      await updateFeatures.mutateAsync({ [key]: toOverride(choice) })
      toast.success(`${FEATURE_CATALOG[key].label} mise à jour pour ce client`)
    } catch (error) {
      toast.error(apiErrorMessage(error, "Modification impossible"))
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Fonctionnalités</CardTitle>
        <CardDescription>« Par défaut » suit le réglage commun à tous les clients.</CardDescription>
      </CardHeader>
      <CardContent className="divide-y divide-border">
        {FEATURE_KEYS.map((key) => {
          const override = overrides[key]
          // Sans exception, la valeur effective est la valeur par défaut : on l'affiche dans l'option « Par défaut ».
          const defaultLabel = override === undefined ? (effective[key] ? "activée" : "désactivée") : null
          return (
            <div key={key} className="flex flex-wrap items-center justify-between gap-3 py-3">
              <div className="min-w-0">
                <p className="font-medium text-foreground">{FEATURE_CATALOG[key].label}</p>
                <p className="text-xs text-muted-foreground">
                  Actuellement {effective[key] ? "activée" : "désactivée"}
                  {override !== undefined ? " (exception pour ce client)" : ""}
                </p>
              </div>
              <Select value={toChoice(override)} onValueChange={(value) => change(key, value as Choice)} disabled={updateFeatures.isPending}>
                <SelectTrigger className="w-48" aria-label={FEATURE_CATALOG[key].label}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">{defaultLabel ? `Par défaut (${defaultLabel})` : "Par défaut"}</SelectItem>
                  <SelectItem value="on">Activée</SelectItem>
                  <SelectItem value="off">Désactivée</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}
